import { useEffect, useState } from "react";
import { Search, Save, RefreshCw, CheckCircle2, XCircle, ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const SITE = "https://comprenahg.com.br";

// prévia estilo Google
const SerpPreview = ({ url, title, description }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 max-w-xl" data-testid="serp-preview">
    <p className="text-[11px] text-slate-400 mb-1 font-semibold uppercase tracking-wide">Prévia no Google</p>
    <div className="flex items-center gap-2">
      <img src="/assets/favicon-32.png" alt="" className="w-6 h-6 rounded-full border border-slate-100" />
      <div>
        <p className="text-xs text-slate-800">HG Móveis</p>
        <p className="text-[11px] text-slate-500">{url}</p>
      </div>
    </div>
    <p className="mt-1.5 text-[#1a0dab] text-lg leading-snug hover:underline cursor-pointer truncate">
      {title || "Título da página"}
    </p>
    <p className="text-sm text-slate-600 leading-snug line-clamp-2">
      {description || "Descrição da página aparece aqui..."}
    </p>
  </div>
);

export default function AdminSeo() {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [seo, setSeo] = useState({ home_title: "", home_description: "" });
  const [catForms, setCatForms] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = () => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setSeo(r.data.seo || { home_title: "", home_description: "" });
    });
    api.get("/categories").then((r) => {
      const cats = r.data;
      setCategories(cats);
      const forms = {};
      cats.forEach((c) => {
        forms[c.id] = { seo_title: c.seo_title || "", seo_description: c.seo_description || "" };
      });
      setCatForms(forms);
    });
  };
  useEffect(load, []);

  const runChecks = () => {
    setChecking(true);
    api.get("/admin/seo/status").then((r) => setStatus(r.data)).catch((err) => toast.error(formatApiError(err))).finally(() => setChecking(false));
  };
  useEffect(runChecks, []);

  const saveHome = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { seo });
      toast.success("Título e descrição da página inicial salvos!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveCat = async (cat) => {
    try {
      const { id, created_at, category, ...rest } = cat;
      await api.put(`/admin/categories/${id}`, { ...rest, ...catForms[id] });
      toast.success(`SEO de "${cat.name}" salvo!`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-5xl" data-testid="admin-seo">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">SEO — Google</h1>
      <p className="text-sm text-gray-500 mt-1 mb-6">
        Como a loja aparece nas pesquisas. Tudo editável aqui, sem mexer em código. O Google decide sozinho quando atualiza os resultados — após publicar, solicite a reindexação no Search Console.
      </p>

      {/* verificação de indexação */}
      <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="seo-status">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
            <Globe className="w-4 h-4 text-brand" /> Verificação do domínio {status?.domain ? `(${status.domain})` : ""}
          </h2>
          <Button variant="outline" size="sm" onClick={runChecks} disabled={checking} data-testid="seo-recheck">
            <RefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} /> Verificar novamente
          </Button>
        </div>
        {!status ? (
          <div className="animate-pulse h-24 bg-slate-50 rounded-lg mt-4" />
        ) : (
          <div className="mt-4 space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Endereços (www e http)</p>
              <ul className="grid sm:grid-cols-2 gap-2">
                {status.variants.map((v) => (
                  <li key={v.url} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`seo-variant-${v.url.replace(/[^a-z]/g, "")}`}>
                    {v.ok ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                    <span className="truncate">{v.url}</span>
                    <span className="text-xs text-slate-400 ml-auto shrink-0">{v.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Indexação e metadados</p>
              <ul className="space-y-2">
                {status.checks.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`seo-check-${i}`}>
                    {c.ok ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800">{c.name}</p>
                      <p className="text-xs text-slate-500 break-words">{c.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-[11px] text-slate-400">
              Verificado em {new Date(status.checked_at).toLocaleString("pt-BR")} — mede o site NO AR. Posições e termos de pesquisa só aparecem no Google Search Console.
            </p>
          </div>
        )}
      </section>

      {/* página inicial */}
      <section className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="seo-home">
        <h2 className="font-heading font-semibold text-gray-900">Página inicial</h2>
        <div className="mt-4 grid lg:grid-cols-2 gap-5 items-start">
          <div className="space-y-3">
            <div>
              <Label>Título na pesquisa (até ~70 caracteres)</Label>
              <Input value={seo.home_title} onChange={(e) => setSeo({ ...seo, home_title: e.target.value })} maxLength={70} className="mt-1.5" data-testid="seo-home-title" />
              <p className="text-[11px] text-slate-400 mt-1">{(seo.home_title || "").length}/70</p>
            </div>
            <div>
              <Label>Descrição na pesquisa (até ~160 caracteres)</Label>
              <Textarea rows={3} value={seo.home_description} onChange={(e) => setSeo({ ...seo, home_description: e.target.value })} maxLength={160} className="mt-1.5 text-sm" data-testid="seo-home-description" />
              <p className="text-[11px] text-slate-400 mt-1">{(seo.home_description || "").length}/160</p>
            </div>
            <Button onClick={saveHome} disabled={saving} className="rounded-full" data-testid="seo-home-save">
              <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar página inicial"}
            </Button>
          </div>
          <SerpPreview url={SITE} title={seo.home_title} description={seo.home_description} />
        </div>
      </section>

      {/* categorias */}
      <section className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="seo-categories">
        <h2 className="font-heading font-semibold text-gray-900">Categorias</h2>
        <p className="text-xs text-slate-500 mt-1">Vazio = texto profissional gerado automaticamente. Preencha para personalizar.</p>
        <div className="mt-4 space-y-4">
          {categories.map((c) => (
            <div key={c.id} className="border border-slate-100 rounded-lg p-4" data-testid={`seo-cat-${c.slug}`}>
              <p className="text-sm font-semibold text-gray-800">
                {c.name} <span className="text-xs font-normal text-slate-400">/categoria/{c.slug}</span>
              </p>
              <div className="mt-2 grid lg:grid-cols-2 gap-4 items-start">
                <div className="space-y-2">
                  <Input
                    value={catForms[c.id]?.seo_title || ""}
                    onChange={(e) => setCatForms({ ...catForms, [c.id]: { ...catForms[c.id], seo_title: e.target.value } })}
                    placeholder={`${c.name} | HG Móveis`}
                    maxLength={70}
                    data-testid={`seo-cat-title-${c.slug}`}
                  />
                  <Textarea
                    rows={2}
                    value={catForms[c.id]?.seo_description || ""}
                    onChange={(e) => setCatForms({ ...catForms, [c.id]: { ...catForms[c.id], seo_description: e.target.value } })}
                    placeholder="Descrição da categoria para o Google"
                    maxLength={160}
                    className="text-sm"
                    data-testid={`seo-cat-desc-${c.slug}`}
                  />
                  <Button size="sm" variant="outline" onClick={() => saveCat(c)} data-testid={`seo-cat-save-${c.slug}`}>
                    <Save className="w-3.5 h-3.5" /> Salvar
                  </Button>
                </div>
                <SerpPreview
                  url={`${SITE}/categoria/${c.slug}`}
                  title={catForms[c.id]?.seo_title || `${c.name} | HG Móveis`}
                  description={catForms[c.id]?.seo_description || `${c.name} com ótimos preços na HG Móveis. Pagamento na entrega em regiões do RJ.`}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* produtos + arquivos */}
      <section className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="seo-files">
        <h2 className="font-heading font-semibold text-gray-900">Produtos e arquivos de indexação</h2>
        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li className="flex items-center gap-2">
            <Search className="w-4 h-4 text-brand shrink-0" />
            SEO de cada produto: edite em <strong>Produtos → editar produto → seção "SEO (Google)"</strong> (título e descrição próprios).
          </li>
          <li>
            <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand hover:underline" data-testid="seo-sitemap-link">
              <ExternalLink className="w-4 h-4" /> Ver sitemap.xml (atualizado sozinho a cada produto/categoria/página)
            </a>
          </li>
          <li>
            <a href="/robots.txt" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand hover:underline" data-testid="seo-robots-link">
              <ExternalLink className="w-4 h-4" /> Ver robots.txt
            </a>
          </li>
        </ul>
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 leading-relaxed">
          <strong>Para o Google esquecer o resultado antigo do WordPress:</strong> após publicar, cadastre o domínio no
          Google Search Console (search.google.com/search-console), envie o sitemap e use "Inspeção de URL → Solicitar indexação"
          na página inicial e nas categorias principais. A atualização dos resultados depende do Google (dias a semanas).
        </div>
      </section>
    </div>
  );
}
